'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { signUp } from './auth-client';
import { Button } from '@/platform/ui/button';
import { Input } from '@/platform/ui/input';
import { Label } from '@/platform/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/platform/ui/card';
import { Alert, AlertDescription } from '@/platform/ui/alert';
import { Mail } from 'lucide-react';

export function SignupForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [invitationCode, setInvitationCode] = useState<string | null>(null);
  const [invitationData, setInvitationData] = useState<any>(null);

  const redirectUrl = searchParams.get('redirect') || '';
  const surveyToken = searchParams.get('survey_token') || '';
  const isSurveyFlow = !!surveyToken;

  useEffect(() => {
    const code = searchParams.get('invitation_code');
    if (code) {
      setInvitationCode(code);

      fetch(`/api/invitations/validate?code=${code}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.valid && data.invitation) {
            setInvitationData(data.invitation);
            setEmail(data.invitation.email);
          }
        })
        .catch((err) => {
          console.error('Error validating invitation:', err);
        });
    }

    const prefillEmail = searchParams.get('email');
    if (prefillEmail && !email) {
      setEmail(prefillEmail);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!firstName || !lastName) {
      setError('Please enter your first and last name');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await signUp(email, password, firstName, lastName, invitationCode || undefined);
      if (redirectUrl) {
        router.push(redirectUrl);
        return;
      }
      if (process.env.NEXT_PUBLIC_REQUIRE_ONBOARDING !== 'true') {
        router.push('/auth/login');
        return;
      }
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Mail className="h-16 w-16 text-primary" />
          </div>
          <CardTitle>Check Your Email</CardTitle>
          <CardDescription>
            We've sent a verification link to {email}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertDescription>
              Please click the verification link in your email to activate your account.
              You must verify your email before you can sign in.
            </AlertDescription>
          </Alert>
          <p className="text-sm text-muted-foreground text-center">
            Didn't receive the email? Check your spam folder or request a new verification link.
          </p>
        </CardContent>
        <CardFooter className="flex flex-col space-y-2">
          <Button
            onClick={() => router.push('/auth/resend-verification')}
            variant="outline"
            className="w-full"
          >
            Resend Verification Email
          </Button>
          <Button
            variant="link"
            className="w-full"
            onClick={() => router.push('/auth/login')}
          >
            Back to Login
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>{isSurveyFlow ? 'Create Account to Start Survey' : 'Create Account'}</CardTitle>
        <CardDescription>
          {invitationData
            ? `You've been invited to join ${invitationData.organization_id}`
            : isSurveyFlow
            ? 'Create an account to begin your assessment'
            : 'Sign up to get started'}
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {invitationData && (
            <Alert>
              <AlertDescription>
                Complete your registration to accept the invitation and access your account.
              </AlertDescription>
            </Alert>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                type="text"
                placeholder="John"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Doe"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading || !!invitationData}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              minLength={6}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
              minLength={6}
            />
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-2">
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Creating account...' : invitationData ? 'Accept Invitation' : 'Create Account'}
          </Button>
          <Button
            type="button"
            variant="link"
            className="w-full"
            onClick={() => {
              const params = new URLSearchParams();
              if (email) params.set('email', email);
              if (redirectUrl) params.set('redirect', redirectUrl);
              if (surveyToken) params.set('survey_token', surveyToken);
              const qs = params.toString();
              router.push(`/auth/login${qs ? `?${qs}` : ''}`);
            }}
          >
            Already have an account? Sign in
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
